import { ref, onMounted, onUnmounted, Ref } from 'vue';
// Use optimized GSAP imports for tree-shaking
import {
  gsap,
  Expo,
  Bounce,
  Elastic,
  Power4,
  ScrollTrigger,
  type Tween,
  type Timeline,
  type TweenVars,
  type StaggerVars,
  type ScrollTriggerInstance,
} from '@/lib/gsap-imports';

// Note: Plugin registration is now handled in gsap-imports.ts
// No need to register here - avoiding duplicate registration

interface GSAPAnimationOptions {
  defaultDuration?: number;
  defaultEase?: string;
  scrollTrigger?: boolean;
}

interface AnimationUtilities {
  animate: (
    target: string | Element | Element[] | null,
    props: TweenVars,
    options?: TweenVars
  ) => Tween;
  spinAndFade: (object: string | Element, duration?: number) => Tween;
  reverseSpinAndFade: (object: string | Element, duration?: number) => Tween;
  swipeAnimation: (
    direction: string,
    distance?: number,
    duration?: number
  ) => Tween;
  animateBoardSwipeLeft: () => Tween;
  animateBoardSwipeRight: () => Tween;
  animateBoardSwipeUp: () => Tween;
  animateBoardSwipeDown: () => Tween;
  animateBoardSwipeIn: () => Tween;
  createScrollAnimation: (
    target: string | Element,
    props: TweenVars,
    triggerOptions?: ScrollTriggerInstance
  ) => Tween | undefined;
  staggerAnimation: (
    targets: string | Element | Element[],
    props: TweenVars,
    staggerOptions?: StaggerVars // <-- use StaggerVars
  ) => Timeline;
  revealText: (target: string | Element, duration?: number) => Tween;
  timeline: Timeline;
  container: Ref<Element | null>;
  tweens: typeof Tweens;
}

type Tweens = {
  Expo: typeof Expo;
  Bounce: typeof Bounce;
  Elastic: typeof Elastic;
  Power4: typeof Power4;
};

export function useGSAPAnimations(
  options: GSAPAnimationOptions = {}
): AnimationUtilities {
  const {
    defaultDuration = 0.4,
    defaultEase = 'expo.inOut',
    scrollTrigger = false,
  } = options;

  const timeline: Timeline = gsap.timeline();
  const container: Ref<Element | null> = ref(null);

  const animations: Ref<Tween[]> = ref([]);
  const tweens: Tweens = { Expo, Bounce, Elastic, Power4 };

  // const typingAnimation = new TypingAnimation(); // TypingAnimation should be properly typed and imported

  const createAnimation = (
    target: string | Element | Element[],
    props: TweenVars,
    options: TweenVars = {}
  ): Tween => {
    const anim = gsap.to(target, { ...props, ...options });
    animations.value.push(anim);
    return anim;
  };

  const animate = (
    target: string | Element | Element[] | null,
    props: TweenVars,
    options: TweenVars = {}
  ): Tween => {
    return createAnimation(
      target,
      {
        duration: defaultDuration,
        ease: defaultEase,
        ...props,
      },
      options
    );
  };

  const spinAndFade = (
    object: string | Element,
    duration: number = defaultDuration
  ): Tween => {
    return animate(object, {
      opacity: 0,
      rotation: 360,
      scale: 0,
      duration: duration,
    });
  };

  const reverseSpinAndFade = (
    object: string | Element,
    duration: number = defaultDuration
  ): Tween => {
    return animate(object, {
      opacity: 1,
      rotation: 0,
      scale: 1,
      duration: duration,
    });
  };

  const swipeAnimation = (
    direction: string,
    distance: number = 3000,
    duration: number = 1.8
  ): Tween => {
    const props: TweenVars = {
      [direction]: distance,
      duration: duration,
      ease: 'power4.inOut',
    };
    return animate(container.value, props);
  };

  const animateBoardSwipeLeft = (): Tween => swipeAnimation('x', -3000);
  const animateBoardSwipeRight = (): Tween => swipeAnimation('x', 3000);
  const animateBoardSwipeUp = (): Tween => swipeAnimation('y', -3000);
  const animateBoardSwipeDown = (): Tween => swipeAnimation('y', 3000);

  const animateBoardSwipeIn = (): Tween => {
    return animate(container.value, {
      x: 0,
      duration: 1.8,
      ease: 'power4.inOut',
    });
  };

  const createScrollAnimation = (
    target: string | Element,
    props: TweenVars,
    triggerOptions: ScrollTriggerInstance = {}
  ): Tween | undefined => {
    if (!scrollTrigger) {
      console.warn('ScrollTrigger is not enabled. Enable it in the options.');
      return;
    }

    return gsap.to(target, {
      ...props,
      scrollTrigger: {
        trigger: target,
        ...triggerOptions,
      },
    });
  };

  const staggerAnimation = (
    targets: string | Element | Element[],
    props: TweenVars,
    staggerOptions?: StaggerVars
  ): Timeline => {
    return gsap.to(targets, {
      ...props,
      stagger: staggerOptions,
    });
  };

  const revealText = (
    target: string | Element,
    duration: number = 1
  ): Tween => {
    return animate(target, {
      opacity: 1,
      y: 0,
      duration: duration,
      ease: 'power2.out',
    });
  };

  onMounted(() => {
    if (scrollTrigger) {
      ScrollTrigger.refresh();
    }
  });

  onUnmounted(() => {
    animations.value.forEach((anim) => anim.kill());
    if (scrollTrigger) {
      ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
    }
  });

  return {
    animate,
    spinAndFade,
    reverseSpinAndFade,
    swipeAnimation,
    animateBoardSwipeLeft,
    animateBoardSwipeRight,
    animateBoardSwipeUp,
    animateBoardSwipeDown,
    animateBoardSwipeIn,
    createScrollAnimation,
    staggerAnimation,
    revealText,
    timeline,
    container,
    tweens,
    // typingAnimation, // Ensure this is properly typed and initialized if utilized
  };
}
